import { Camera, Heart, MapPin, Phone } from "lucide-react";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTrackClicks } from "../../../hooks/useAnalyticsTracker.js";
import {
  formatPrettyAddress,
  formatPrice,
  getPrice,
  timeAgo,
} from "../../../utils/helpers.js";

const PropertyCard = ({ property, openModal, setPropertyData }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [liked, setLiked] = useState(false);
  const { trackViewClick, trackContactClick } = useTrackClicks(property._id);

  const handleView = () => {
    trackViewClick();

    const type = property.type;

    // CURRENT PATH
    const path = location.pathname;

    // CASE 1 → Coming from /properties/{type}
    // Example: /properties/land, /properties/commercial
    if (path.startsWith(`/properties/${type}`)) {
      navigate(`/properties/${type}/${property._id}`);
    }

    // CASE 2 → Coming from generic /properties
    else if (path.startsWith("/properties")) {
      navigate(`/properties/${type}/${property._id}`);
    }

    // CASE 3 → Coming from somewhere else (homepage, search results, trending)
    else {
      navigate(`/properties/${type}/${property._id}`);
    }

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleContact = () => {
    openModal();
    trackContactClick();
    setPropertyData(property);
  };

  return (
    <div className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 flex flex-col md:flex-row overflow-hidden">
      {/* IMAGE SECTION */}
      <CardImage images={property.images} liked={liked} setLiked={setLiked} />

      {/* CONTENT SECTION */}
      <div className="flex-1 p-4 md:p-5 flex flex-col justify-between">
        {/* TOP CONTENT BLOCK */}
        <div className="flex flex-col gap-3">
          {/* HEADER */}
          <div>
            <div className="flex justify-between items-center">
              {property.title && (
                <h3 className="text-lg md:text-xl font-semibold text-gray-900 leading-tight">
                  {property.title}
                </h3>
              )}
              <p className="text-xs md:text-sm text-gray-500">
                {timeAgo(property.createdAt)}
              </p>
            </div>

            <div className="flex justify-between items-center mt-1">
              {formatPrettyAddress(property.location) && (
                <p className="text-xs md:text-sm text-gray-600 flex items-center gap-1">
                  <MapPin size={13} />
                  {formatPrettyAddress(property.location)}
                </p>
              )}
            </div>
          </div>

          {/* HIGHLIGHTS */}
          <PropertyHighlights property={property} />

          {/* DESCRIPTION */}
          {property.description && (
            <p className="text-xs md:text-sm text-gray-600 line-clamp-2">
              {property.description}
            </p>
          )}

          {property.listedBy && (
            <div className="md:hidden">
              <AgentInfo listedBy={property.listedBy} />
            </div>
          )}
        </div>

        {/* ACTION BUTTONS - ALWAYS BOTTOM RIGHT */}
        <div className="flex md:justify-between justify-end mt-4">
          {property.listedBy && (
            <div className="hidden md:block">
              <AgentInfo listedBy={property.listedBy} />
            </div>
          )}
          <CardActions
            type={property.type}
            onContact={handleContact}
            onView={handleView}
          />
        </div>
      </div>
    </div>
  );
};

export default PropertyCard;

const CardImage = ({ images, liked, setLiked }) => {
  const thumbnailImg =
    images?.find((img) => img.isThumbnail)?.url ||
    images?.find((img) => img.category === "exterior")?.url ||
    images?.[0]?.url ||
    null;

  return (
    <div className="relative w-full md:w-1/3 aspect-[4/3] bg-gray-100 overflow-hidden">
      {thumbnailImg ? (
        <img
          src={thumbnailImg}
          alt="Property"
          className="w-full h-full object-cover"
        />
      ) : (
        <div className="flex items-center justify-center w-full h-full text-gray-400 text-sm">
          No Image
        </div>
      )}

      {/* HEART BUTTON */}
      <button
        className="absolute top-3 right-3 bg-white p-2 rounded-full shadow-md hover:scale-110 transition"
        onClick={(e) => {
          e.stopPropagation();
          setLiked(!liked);
        }}
      >
        <Heart
          className={`w-5 h-5 ${
            liked ? "fill-red-500 text-red-500" : "text-gray-400"
          }`}
        />
      </button>

      {/* IMAGE COUNT */}
      {images?.length > 0 && (
        <div className="absolute bottom-3 left-3 bg-black/60 text-white px-2 py-1 rounded-full flex items-center gap-1 text-xs">
          <Camera size={14} />
          <span>{images.length}</span>
        </div>
      )}
    </div>
  );
};

const PropertyHighlights = ({ property }) => {
  const { type, property: prop } = property;
  const price = getPrice(property);
  const formattedPrice = price ? formatPrice(price) : null;
  const capitalize = (str) =>
    typeof str === "string" ? str.charAt(0).toUpperCase() + str.slice(1) : str;

  const list = [];

  if (type === "residential") {
    if (formattedPrice) list.push({ label: "Price", value: formattedPrice });
    if (prop.floorPlan?.carpetArea?.value)
      list.push({
        label: "Carpet Area",
        value:
          prop.floorPlan.carpetArea.value +
          " " +
          prop.floorPlan.carpetArea.unit,
      });
    if (prop.floorPlan?.bhk)
      list.push({ label: "BHK", value: prop.floorPlan.bhk });
  }

  if (type === "commercial") {
    const area = prop.allowedForConstructionDetails?.carpetArea;
    if (formattedPrice) list.push({ label: "Price", value: formattedPrice });
    if (area?.value)
      list.push({
        label: "Built-up",
        value: area.value + " " + area.unit,
      });
    if (prop.allowedForConstructionDetails?.floor)
      list.push({
        label: "Floor",
        value: prop.allowedForConstructionDetails.floor,
      });
  }

  if (type === "land") {
    if (formattedPrice) list.push({ label: "Price", value: formattedPrice });
    if (prop.carpetArea?.value)
      list.push({
        label: "Plot Area",
        value: prop.carpetArea.value + " " + prop.carpetArea.unit,
      });
    if (property.listingType)
      list.push({ label: "For", value: capitalize(property.listingType) });
  }

  if (type === "project") {
    if (formattedPrice) list.push({ label: "Price", value: formattedPrice });
    if (prop.floorPlan?.bhk)
      list.push({ label: "BHK", value: prop.floorPlan.bhk });
    if (prop.floorPlan?.carpetArea?.value)
      list.push({
        label: "Area",
        value:
          prop.floorPlan.carpetArea.value +
          " " +
          prop.floorPlan.carpetArea.unit,
      });
  }

  if (list.length === 0) return null;

  return (
    <div className="grid grid-cols-3 gap-3 py-2 text-sm md:text-base">
      {list.map((item, i) => (
        <Highlight key={i} label={item.label} value={item.value} />
      ))}
    </div>
  );
};

const Highlight = ({ label, value }) =>
  value ? (
    <div>
      <p className="font-semibold text-gray-900">{value}</p>
      <p className="text-gray-500 text-xs">{label}</p>
    </div>
  ) : null;

const AgentInfo = ({ listedBy }) => {
  const firstLetter = listedBy?.fullName?.charAt(0);
  // console.log("listedby:", listedBy);
  if (!listedBy) return null;

  const roleMap = {
    "buyer-owner": "Owner",
    "agent-builder": "Agent",
    "super-admin": "Admin",
  };

  const cleanRole = roleMap[listedBy.role] || null;

  return (
    <div className="flex items-center self-end md:self-auto gap-1 md:gap-3 bg-[#FFE8E4] p-1 md:p-2 rounded-tr-3xl  md:px-4  w-fit">
      <div className="w-6 h-6 md:w-10  md:h-10 bg-gray-900 text-white flex items-center justify-center rounded-full text-sm md:text-base md:font-bold">
        {firstLetter}
      </div>

      <div className="flex md:flex-col">
        {listedBy.fullName && (
          <p className="font-medium text-gray-900 text-xs md:text-sm">
            {listedBy.fullName}
          </p>
        )}
        {cleanRole && (
          <p className="md:hidden ml-1 text-gray-500 text-[0.6rem] my-auto">
            {" "}
            ({cleanRole})
          </p>
        )}
        {cleanRole && (
          <p className="hidden md:block text-gray-500 text-xs">{cleanRole}</p>
        )}
      </div>
    </div>
  );
};

const CardActions = ({ type, onContact, onView }) => {
  return (
    <div className="flex gap-2 items-end  flex-wrap justify-end">
      {/* SHARED BUTTON CLASS */}
      <button
        className="
          px-4 
          py-2 
          min-h-[40px] 
          md:min-h-[44px]
          min-w-[110px]
          md:min-w-[130px]
          bg-white 
          self-end
          border-2 
          border-red-500 
          text-red-500 
          rounded-lg 
          text-xs 
          md:text-sm 
          font-semibold 
          flex 
          items-center 
          justify-center 
          gap-1 
          hover:bg-red-50
        "
        onClick={onContact}
      >
        <Phone size={14} />
        {type === "commercial" || type === "land"
          ? "View Number"
          : "Contact Agent"}
      </button>

      <button
        className="
          px-4 
          self-end
          py-2 
          min-h-[40px] 
          md:min-h-[44px]
          min-w-[110px]
          md:min-w-[130px]
          bg-red-500 
          text-white 
          rounded-lg 
          text-xs 
          md:text-sm 
          font-semibold 
          flex 
          items-center 
          justify-center 
          gap-1 
          hover:bg-red-600
        "
        onClick={onView}
      >
        <Phone size={14} />
        View Details
      </button>
    </div>
  );
};
